import { Helmet } from 'react-helmet-async';
import { baseSEO, generateStructuredData } from '@/config/seoConfig';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string | string[];
  canonicalUrl?: string;
  ogImage?: string;
  type?: 'website' | 'article' | 'product';
  schema?: any;
  breadcrumbs?: Array<{ name: string; url: string }>;
  noIndex?: boolean;
  locale?: string;
}

export const SEO = ({ 
  title = "Château de Clémentine - Location Châteaux Gonflables & Sumos en Belgique",
  description = "Location de châteaux gonflables et sumos à Rhode-Saint-Genèse depuis 1993. Prix à partir de 30€ pour le week-end complet. Retrait facile, montage simple en 10min.",
  keywords = "château gonflable, location château, sumo, anniversaire enfant, fête enfant, Rhode-Saint-Genèse, Belgique, Bruxelles, Brabant Wallon",
  canonicalUrl = baseSEO.baseUrl,
  ogImage = baseSEO.defaultImage,
  type = "website",
  schema,
  breadcrumbs,
  noIndex = false,
  locale = baseSEO.locale
}: SEOProps) => {
  
  // Process keywords
  const keywordArray = Array.isArray(keywords) ? keywords : keywords.split(', ');
  const keywordsString = keywordArray.join(', ');
  
  // Default structured data
  const defaultSchema = type === 'website' 
    ? generateStructuredData.organization()
    : null;
  
  const structuredData = schema || defaultSchema;
  
  // Generate breadcrumb schema if provided
  const breadcrumbSchema = breadcrumbs 
    ? generateStructuredData.breadcrumb(breadcrumbs)
    : null;

  // Ensure ogImage is absolute URL
  const absoluteOgImage = ogImage?.startsWith('http') 
    ? ogImage 
    : `${baseSEO.baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={locale.replace('_', '-')} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywordsString} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content={locale} />
      <meta name="author" content={baseSEO.siteName} />
      <meta charSet={baseSEO.charset} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={baseSEO.siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Performance and Security */}
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />
      <meta httpEquiv="content-security-policy" content="upgrade-insecure-requests" />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};
